import { spawn } from "node:child_process";
import net from "node:net";

import type { DirectEndpointCandidate } from "./relay-dialer.js";

const SSH_PROBE_TIMEOUT_MS = 2_500;
const SSH_COMMAND_TIMEOUT_MS = 120_000;

export type CrossHostRouteKind = "ssh" | "ssh-setup-needed" | "appostle";

export interface CrossHostRouteTarget {
  name: string;
  serverId: string;
  directEndpoints?: DirectEndpointCandidate[];
}

export interface CrossHostRouteDecision {
  kind: CrossHostRouteKind;
  reason: string;
  sshHost?: string;
  setupHost?: string;
}

export interface CrossHostRouteProgressEvent {
  stage:
    | "target"
    | "ssh-profile"
    | "lan-endpoint"
    | "ssh-selected"
    | "ssh-setup-needed"
    | "appostle-selected";
  message: string;
  route?: CrossHostRouteKind;
  host?: string;
}

export interface SshProbeResult {
  ok: boolean;
  reason: string;
}

export interface CrossHostRouteProbe {
  probeSshAuth(host: string): Promise<SshProbeResult>;
  probeTcpPort(host: string, port: number): Promise<boolean>;
}

export interface SshExecResult {
  stdout: string;
  stderr: string;
  code: number | null;
  signal: string | null;
  error: string | null;
}

export interface SshTransferResult {
  stdout: string;
  stderr: string;
  code: number | null;
  signal: string | null;
  error: string | null;
}

export function buildDefaultCrossHostRouteProbe(): CrossHostRouteProbe {
  return {
    probeSshAuth: probeSshAuthHost,
    probeTcpPort: probeTcpPort,
  };
}

export async function resolveCrossHostRoute(
  target: CrossHostRouteTarget,
  probe: CrossHostRouteProbe = buildDefaultCrossHostRouteProbe(),
  onProgress?: (event: CrossHostRouteProgressEvent) => void,
): Promise<CrossHostRouteDecision> {
  onProgress?.({
    stage: "target",
    message: `Found ${target.name} (${target.serverId}); checking available transports.`,
  });
  const aliasCandidates = uniqueNonEmpty([target.name, target.serverId]);
  for (const host of aliasCandidates) {
    onProgress?.({
      stage: "ssh-profile",
      host,
      message: `Checking SSH profile or alias "${host}" with BatchMode auth.`,
    });
    const result = await probe.probeSshAuth(host);
    if (result.ok) {
      onProgress?.({
        stage: "ssh-selected",
        route: "ssh",
        host,
        message: `Using SSH profile "${host}".`,
      });
      return {
        kind: "ssh",
        sshHost: host,
        reason: `existing SSH profile "${host}" works with BatchMode auth`,
      };
    }
  }

  for (const host of directEndpointHosts(target.directEndpoints ?? [])) {
    onProgress?.({
      stage: "lan-endpoint",
      host,
      message: `Checking LAN SSH endpoint ${host}:22 with BatchMode auth.`,
    });
    const result = await probe.probeSshAuth(host);
    if (result.ok) {
      onProgress?.({
        stage: "ssh-selected",
        route: "ssh",
        host,
        message: `Using LAN SSH endpoint ${host}:22.`,
      });
      return {
        kind: "ssh",
        sshHost: host,
        reason: `LAN SSH to ${host}:22 works with existing credentials`,
      };
    }
    if (await probe.probeTcpPort(host, 22)) {
      onProgress?.({
        stage: "ssh-setup-needed",
        route: "ssh-setup-needed",
        host,
        message: `LAN SSH is reachable at ${host}:22, but BatchMode auth is not configured.`,
      });
      return {
        kind: "ssh-setup-needed",
        setupHost: host,
        reason: `LAN SSH is reachable at ${host}:22, but BatchMode auth is not configured (${result.reason})`,
      };
    }
  }

  onProgress?.({
    stage: "appostle-selected",
    route: "appostle",
    message: "No working local SSH route found; using Appostle E2EE transport.",
  });
  return {
    kind: "appostle",
    reason: "no working local SSH route found; using Appostle E2EE transport",
  };
}

export async function runSshExec(args: {
  sshHost: string;
  command: string;
  cwd?: string;
  timeoutMs?: number;
  onChunk?: (stream: "stdout" | "stderr", text: string) => void;
}): Promise<SshExecResult> {
  const remoteCommand = args.cwd ? `cd ${shellQuote(args.cwd)} && ${args.command}` : args.command;
  return spawnCapture("ssh", sshBaseArgs(args.sshHost).concat(remoteCommand), {
    timeoutMs: args.timeoutMs ?? SSH_COMMAND_TIMEOUT_MS,
    onChunk: args.onChunk,
  });
}

export async function runSshTransfer(args: {
  sshHost: string;
  direction: "pull" | "push";
  remotePath: string;
  localPath: string;
}): Promise<SshTransferResult> {
  const remote = `${args.sshHost}:${args.remotePath}`;
  const scpArgs =
    args.direction === "pull"
      ? scpBaseArgs().concat(remote, args.localPath)
      : scpBaseArgs().concat(args.localPath, remote);
  return spawnCapture("scp", scpArgs, { timeoutMs: SSH_COMMAND_TIMEOUT_MS });
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value?.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
  }
  return out;
}

function directEndpointHosts(candidates: DirectEndpointCandidate[]): string[] {
  return uniqueNonEmpty(
    candidates.flatMap((candidate) => {
      const endpoint = candidate.endpoint.trim();
      if (!endpoint) return [];
      try {
        return [new URL(endpoint.includes("://") ? endpoint : `ws://${endpoint}`).hostname];
      } catch {
        const bracketMatch = endpoint.match(/^\[([^\]]+)\]/);
        if (bracketMatch) return [bracketMatch[1]];
        return [endpoint.split(":")[0]];
      }
    }),
  );
}

async function probeSshAuthHost(host: string): Promise<SshProbeResult> {
  const result = await spawnCapture("ssh", sshBaseArgs(host).concat("true"), {
    timeoutMs: SSH_PROBE_TIMEOUT_MS,
    maxBytes: 8_192,
  });
  if (result.code === 0) return { ok: true, reason: "ok" };
  const stderr = result.stderr.trim();
  return {
    ok: false,
    reason: stderr || result.error || `ssh exited ${result.code ?? result.signal ?? "unknown"}`,
  };
}

function probeTcpPort(host: string, port: number): Promise<boolean> {
  return new Promise((resolve) => {
    const socket = net.createConnection({ host, port });
    const done = (ok: boolean) => {
      socket.removeAllListeners();
      socket.destroy();
      resolve(ok);
    };
    socket.setTimeout(SSH_PROBE_TIMEOUT_MS);
    socket.once("connect", () => done(true));
    socket.once("timeout", () => done(false));
    socket.once("error", () => done(false));
  });
}

function sshBaseArgs(host: string): string[] {
  return [
    "-o",
    "BatchMode=yes",
    "-o",
    "ConnectTimeout=3",
    "-o",
    "StrictHostKeyChecking=accept-new",
    host,
  ];
}

function scpBaseArgs(): string[] {
  return [
    "-B",
    "-o",
    "BatchMode=yes",
    "-o",
    "ConnectTimeout=3",
    "-o",
    "StrictHostKeyChecking=accept-new",
  ];
}

function shellQuote(value: string): string {
  return `'${value.split("'").join("'\\''")}'`;
}

function spawnCapture(
  command: string,
  args: string[],
  options: {
    timeoutMs: number;
    maxBytes?: number;
    onChunk?: (stream: "stdout" | "stderr", text: string) => void;
  },
): Promise<SshExecResult> {
  return new Promise((resolve) => {
    const child = spawn(command, args, { stdio: ["ignore", "pipe", "pipe"] });
    const stdoutChunks: Buffer[] = [];
    const stderrChunks: Buffer[] = [];
    const maxBytes = options.maxBytes ?? 1024 * 1024;
    let bytes = 0;
    let settled = false;
    let error: string | null = null;
    const timeout = setTimeout(() => {
      error = "ssh command timed out";
      child.kill("SIGTERM");
    }, options.timeoutMs);

    const collect = (stream: "stdout" | "stderr", chunks: Buffer[], chunk: Buffer) => {
      if (bytes >= maxBytes) return;
      const remaining = maxBytes - bytes;
      const slice = chunk.length > remaining ? chunk.subarray(0, remaining) : chunk;
      chunks.push(slice);
      bytes += slice.length;
      options.onChunk?.(stream, slice.toString("utf8"));
    };

    child.stdout?.on("data", (chunk: Buffer) => collect("stdout", stdoutChunks, chunk));
    child.stderr?.on("data", (chunk: Buffer) => collect("stderr", stderrChunks, chunk));
    child.on("error", (err) => {
      error = err instanceof Error ? err.message : String(err);
    });
    child.on("close", (code, signal) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      resolve({
        stdout: Buffer.concat(stdoutChunks).toString("utf8"),
        stderr: Buffer.concat(stderrChunks).toString("utf8"),
        code,
        signal,
        error,
      });
    });
  });
}
